import { formatUploadDate, uploadDateMillis } from './uploadDate';
import React, { useState, useEffect } from 'react';
import { Avatar, Button, Grid, Typography, Link, IconButton, TextField } from '@mui/material';
import { styled } from '@mui/material/styles';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { deleteObject, getDownloadURL, ref, uploadBytes, getMetadata } from 'firebase/storage';
import { v4 as uuidv4 } from 'uuid';
import { storage, db } from '../service/Firebase-config';
import { updateDoc, doc, arrayUnion, Timestamp } from 'firebase/firestore';
import { useAuth } from '../service/AuthContext';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import { useGlobalContext } from '../context/GlobalContext';
import { useHomeworkContext } from '../context/HomeworkContext';
import dayjs from 'dayjs';
import DeleteConfirmation from './DeleteConfirmation';
import SingleSelectTreeView from './SingleSelectTreeView';

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const HomeworkInfo = ({ selectedTeacher = null }) => {
  const { currentUser } = useAuth();
  const { selectedUser } = useGlobalContext();
  const { selectedHw } = useHomeworkContext();

  const [isLoading, setIsLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isReadMode, setIsReadMode] = useState(true);
  const [updatedSubject, setUpdatedSubject] = useState('');
  const [updatedHw, setUpdatedHw] = useState('');
  const [updatedDate, setUpdatedDate] = useState(dayjs());
  const [fileTeacher, setFileTeacher] = useState([]);

  useEffect(() => {
    const loadFilesWithCreatedDate = async () => {
      if (!selectedHw || !selectedUser?.uid) return;

      setUpdatedSubject(selectedHw.subject || '');
      setUpdatedHw(selectedHw.homework || '');
      setUpdatedDate(
        selectedHw.createdDate?.seconds
          ? dayjs(new Date(selectedHw.createdDate.seconds * 1000))
          : dayjs()
      );

      const files = selectedHw.fileTeacher || [];

      if (files.length === 0) {
        setFileTeacher([]);
        return;
      }

      const updatedFiles = await Promise.all(
        files.map(async (file) => {
          if (file.createdDate) {
            return file;
          }

          try {
            const teacherFileRef = ref(
              storage,
              `file/${selectedUser.uid}/${selectedHw.id}/${file.savedName}`
            );

            const metadata = await getMetadata(teacherFileRef);

            return {
              ...file,
              createdDate: metadata.timeCreated || null,
            };
          } catch (error) {
            console.error('Failed to read file metadata:', error);
            return {
              ...file,
              createdDate: null,
            };
          }
        })
      );

      const sortedFiles = [...updatedFiles].sort((a, b) => {
        const aTime = uploadDateMillis(a.createdDate);
        const bTime = uploadDateMillis(b.createdDate);
        return bTime - aTime;
      });

      setFileTeacher(sortedFiles);

      const hasMissingCreatedDate = files.some((file) => !file.createdDate);
      if (hasMissingCreatedDate) {
        try {
          await updateDoc(
            doc(db, `students/${selectedUser.uid}/homeworks/`, selectedHw.id),
            { fileTeacher: sortedFiles }
          );
        } catch (error) {
          console.error('Failed to update fileTeacher with createdDate:', error);
        }
      }
    };

    loadFilesWithCreatedDate();
  }, [selectedHw, selectedUser]);

  const handleSelectSubject = (subject) => {
    setUpdatedSubject(subject);
  };

  const handleFileUpload = async (e) => {
    setIsLoading(true);

    const file = e.target.files[0];
    if (!file || !selectedHw || !selectedUser?.uid) {
      setIsLoading(false);
      return;
    }

    const id = uuidv4();
    const savedName = `${id}${file.name}`;
    const teacherFileRef = ref(storage, `file/${selectedUser.uid}/${selectedHw.id}/${savedName}`);

    try {
      const snapshot = await uploadBytes(teacherFileRef, file);
      const url = await getDownloadURL(snapshot.ref);
      const metadata = await getMetadata(snapshot.ref);

      const newFile = {
        fileName: file.name,
        fileUrl: url,
        savedName,
        createdDate: metadata.timeCreated || new Date().toISOString(),
      };

      setFileTeacher((prev) => {
        const next = prev ? [...prev, newFile] : [newFile];
        return next.sort((a, b) => {
          const aTime = uploadDateMillis(a.createdDate);
          const bTime = uploadDateMillis(b.createdDate);
          return bTime - aTime;
        });
      });

      await updateDoc(
        doc(db, `students/${selectedUser.uid}/homeworks/`, selectedHw.id),
        { fileTeacher: arrayUnion(newFile) }
      );
    } catch (error) {
      console.error('File upload failed:', error);
    } finally {
      e.target.value = '';
      setIsLoading(false);
    }
  };

  const handleRemoveFile = async (file) => {
    if (!file || !selectedHw || !selectedUser?.uid) return;

    setIsLoading(true);
    const teacherFileRef = ref(storage, `file/${selectedUser.uid}/${selectedHw.id}/${file.savedName}`);

    try {
      await deleteObject(teacherFileRef);

      const filteredFile = fileTeacher.filter(
        (item) => item.savedName !== file.savedName
      );

      setFileTeacher(filteredFile);

      await updateDoc(
        doc(db, `students/${selectedUser.uid}/homeworks/`, selectedHw.id),
        { fileTeacher: filteredFile }
      );
    } catch (error) {
      console.error('File deletion failed:', error);
    } finally {
      setSelectedFile(null);
      setIsLoading(false);
    }
  };

  const saveEditedHomework = async () => {
    if (!selectedHw || !selectedUser?.uid) return;

    const updatedData = {
      createdDate: Timestamp.fromDate(updatedDate.toDate()),
      subject: updatedSubject,
      homework: updatedHw,
    };

    await updateDoc(
      doc(db, `students/${selectedUser.uid}/homeworks/`, selectedHw.id),
      updatedData
    );
  };

  return (
    <React.Fragment>
      <Grid container spacing={1}>
        <Grid item xs={12} md={5} lg={6}>
          <Grid
            container
            sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}
          >
            <Grid
              item
              xs={4}
              sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}
            >
              <Avatar
                src={selectedTeacher ? selectedTeacher.photoUrl : currentUser.photoURL}
                sx={{ width: 60, height: 60 }}
              />
              {selectedTeacher ? selectedTeacher.displayName : currentUser.displayName}
            </Grid>

            <Grid item xs={8} sx={{ display: 'flex', flexDirection: 'column' }}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  disabled={isReadMode}
                  value={updatedDate}
                  onChange={(newDate) => setUpdatedDate(newDate)}
                  sx={{ paddingBottom: 2 }}
                />
              </LocalizationProvider>

              <SingleSelectTreeView
                disabled={isReadMode}
                selectedSubject={updatedSubject}
                onSelect={handleSelectSubject}
              />
            </Grid>
          </Grid>

          <Grid item>
            <TextField
              autoFocus
              required
              disabled={isReadMode}
              margin="dense"
              id="homework"
              name="homework"
              label="Homework"
              multiline
              rows={4}
              fullWidth
              variant="outlined"
              value={updatedHw}
              onChange={(e) => setUpdatedHw(e.target.value)}
            />
          </Grid>

          {isReadMode ? (
            <Button variant="contained" fullWidth onClick={() => setIsReadMode(false)}>
              Edit Homework
            </Button>
          ) : (
            <Button
              variant="contained"
              color="info"
              fullWidth
              onClick={() => {
                setIsLoading(true);
                saveEditedHomework()
                  .then(() => {
                    setIsReadMode(true);
                  })
                  .finally(() => {
                    setIsLoading(false);
                  });
              }}
            >
              Save Homework
            </Button>
          )}
        </Grid>

        <Grid
          item
          xs={12}
          md={7}
          lg={6}
          sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}
        >
          <Grid item>
            <Button variant="contained" startIcon={<CloudUploadIcon />} component="label">
              Upload file
              <VisuallyHiddenInput type="file" onChange={handleFileUpload} />
            </Button>
          </Grid>

          {fileTeacher &&
            fileTeacher.map((file) => (
              <Grid
                item
                width="80%"
                p={1}
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  gap: 1,
                  '&:hover': { backgroundColor: '#e3f2fd' },
                }}
                key={file.savedName || file.fileName}
              >
                <Grid sx={{ flex: 1, minWidth: 0 }}>
                  <Link
                    href={file.fileUrl}
                    target="_blank"
                    underline="hover"
                    sx={{
                      display: 'block',
                      wordBreak: 'break-all',
                      whiteSpace: 'normal',
                    }}
                  >
                    {file.fileName}
                  </Link>

                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      mt: 0.5,
                      color: 'text.secondary',
                    }}
                  >
                    {formatUploadDate(file.createdDate)}
                  </Typography>
                </Grid>

                <IconButton
                  color="inherit"
                  size="small"
                  onClick={() => setSelectedFile(file)}
                  sx={{ flexShrink: 0 }}
                >
                  <DeleteRoundedIcon />
                </IconButton>
              </Grid>
            ))}

          {isLoading && <Typography variant="h5">Loading ...</Typography>}
        </Grid>
      </Grid>

      <DeleteConfirmation
        open={Boolean(selectedFile)}
        closeDelete={() => setSelectedFile(null)}
        handleDelete={() => handleRemoveFile(selectedFile)}
        title="Are you sure you want to delete this file?"
      />
    </React.Fragment>
  );
};

export default HomeworkInfo;